import {
  Button,
  Col,
  Drawer,
  Input,
  LegacyForm,
  Row,
  Skeleton,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { Trans } from "@payping/utils/localeUtils";
import { buildUrl } from "@payping/utilities-shared/url";
import { URLS } from "@payping/utilities-shared/consts";
import { useAppSelector, useAppTranslation } from "@/utils";
import { useNavigate } from "react-router-dom";
import { FC } from "react";
import { userApis } from "@payping/api";
import { DashboardRoutes } from "@/router";
import { Close } from "@payping/icons";

interface BusinessAddFormType {
  username: string;
  businessName: string;
}

const MobileBusinessAdd: FC = () => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<BusinessAddFormType>();
  const navigate = useNavigate();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);

  const [{ loading: loadingAdd }, handleAdd] = useMessagedAsyncFn(
    async (values: BusinessAddFormType) => {
      await userApis.registerWithRequestModelWithCancellationTokenPost({
        username: values.username,
        email: `${values.username}@payping.io`,
        phoneNumber: personalInfo?.phoneNumber,
        // @ts-expect-error missing type
        businessName: values.businessName,
      });
      navigate(DashboardRoutes.home.root.url);
    },
    [personalInfo?.phoneNumber, navigate],
    undefined,
    { useProgress: true },
  );

  const loading = loadingAdd;

  return (
    <Drawer
      open={true}
      placement={"bottom"}
      height={"100%"}
      title={t("settings.shop.title")}
      closeIcon={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            navigate(-1);
          }}
        />
      }
      footer={
        <LegacyForm.Item noStyle>
          <Button type={"primary"} className="tw:my-4!" block loading={loading} onClick={form.submit}>
            {t("common.activate")}
          </Button>
        </LegacyForm.Item>
      }
    >
      <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
        <Typography.Title level={4} className="tw:font-medium">
          {t("mainNav.business.addAccount")}
        </Typography.Title>
        <Typography.Text>
          <Trans
            i18nKey={"settings.business.addBusinessHelp"}
            components={{
              a: <a href={buildUrl(URLS.AUTH_URL, "/auth/number").toString()} target={"_blank"} rel="noreferrer" />,
            }}
          />
        </Typography.Text>
        <Row className={"tw:w-full"}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <LegacyForm<BusinessAddFormType> onFinish={handleAdd} form={form} layout="vertical">
              <LegacyForm.Item
                rules={[
                  { required: true },
                  {
                    pattern: /^[a-zA-Z0-9]+$/,
                    message: t("common.validations.onlyEnglishLetterNumerals"),
                  },
                ]}
                name={"username"}
                label={t("common.username")}
              >
                {loading ? <Skeleton.Input /> : <Input />}
              </LegacyForm.Item>
              <LegacyForm.Item
                rules={[
                  { required: true },
                  {
                    pattern: /^[\u0600-\u06FF\s]+$/,
                    message: t("common.validations.onlyPersianLetters"),
                  },
                ]}
                name={"businessName"}
                label={t("settings.business.businessName")}
              >
                {loading ? <Skeleton.Input /> : <Input />}
              </LegacyForm.Item>
            </LegacyForm>
          </Col>
        </Row>
      </Space>
    </Drawer>
  );
};

export default MobileBusinessAdd;
